<template>
  <div class="page">
    <header class="home-intro">
      <h2 class="home-intro__title">{{ $t('home.sectionTitleText') }}</h2>
      <p class="home-intro__subtitle">{{ $t('home.slogan') }}</p>
    </header>

    <section class="games-panel">
      <div v-if="!games.length" class="games__empty">
        <i class="el-icon-box games__empty-icon"></i>
        <p>{{ $t('home.noGames') }}</p>
      </div>

      <div v-else class="games__grid">
        <article
          v-for="game in games"
          :key="game.id"
          class="game-card"
          role="button"
          tabindex="0"
          @click="openPurchase(game)"
          @keyup.enter="openPurchase(game)"
        >
          <div class="game-card__media">
            <img
              :class="['game-card__cover', { 'game-card__cover--link': hasCoverLink(game) }]"
              :src="coverUrl(game.cover)"
              :alt="game.name"
              loading="lazy"
              @click.stop="openCoverLink(game)"
            >
            <div class="game-card__shade"></div>
            <span class="game-card__badge">{{ $t('home.cardBadge') }}</span>
          </div>
          <div class="game-card__body">
            <h3 class="game-card__name" :title="game.name">{{ game.name }}</h3>
            <div class="game-card__footer">
              <span class="game-card__price">{{ formatPrice(game.price) }}</span>
              <span class="game-card__action">
                {{ $t('gamesPurchase.pickGame') }}
              </span>
            </div>
          </div>
        </article>
      </div>
    </section>

    <GamePurchaseDialogs ref="gamePurchaseDialogs" />
  </div>
</template>

<script>
import GamePurchaseDialogs from '@/components/GamePurchaseDialogs.vue'
import { getAuthHeader, isLoggedIn, redirectToLogin } from '@/utils/auth'

function useBackend() {
  return process.env.VUE_APP_USE_BACKEND === 'true'
}

function apiUrl(path) {
  const root = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  return root ? `${root}${p}` : p
}

function firstCoverPath(u) {
  const s = String(u || '').trim()
  if (!s) return ''
  if (s.startsWith('[')) {
    try {
      const a = JSON.parse(s)
      if (Array.isArray(a) && a.length) return String(a[0] || '').trim()
    } catch (e) {
      return ''
    }
    return ''
  }
  return s
}

function coverUrl(u) {
  const p = firstCoverPath(u)
  if (!p) return ''
  if (/^https?:\/\//i.test(p)) return p
  const root = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
  if (!root) return p.startsWith('/') ? p : `/${p}`
  return p.startsWith('/') ? `${root}${p}` : `${root}/${p}`
}

function normalizeCoverLink(game) {
  const link = String((game && game.coverLink) || '').trim()
  return /^https?:\/\//i.test(link) ? link : ''
}

export default {
  name: 'GamesPage',
  components: {
    GamePurchaseDialogs
  },
  data() {
    return {
      games: []
    }
  },
  async mounted() {
    if (!useBackend()) return
    try {
      const res = await fetch(apiUrl('/api/games'), {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      })
      const data = await res.json()
      if (data && data.ok && Array.isArray(data.data)) {
        this.games = data.data
      }
    } catch (e) {
      /* keep empty list */
    }
  },
  methods: {
    formatPrice(value) {
      const amount = Number(value)
      if (Number.isNaN(amount)) return '¥0.00'
      return new Intl.NumberFormat('zh-CN', {
        style: 'currency',
        currency: 'CNY'
      }).format(amount)
    },
    coverUrl,
    hasCoverLink(game) {
      return Boolean(normalizeCoverLink(game))
    },
    openCoverLink(game) {
      const link = normalizeCoverLink(game)
      if (!link) {
        this.openPurchase(game)
        return
      }
      window.open(link, '_blank', 'noopener')
    },
    openPurchase(game) {
      if (!useBackend()) {
        this.$message.warning(this.$t('gamesPurchase.needBackend'))
        return
      }
      if (!isLoggedIn()) {
        this.$message.error(this.$t('gamesPurchase.needLogin'))
        redirectToLogin(this.$router, this.$route.fullPath)
        return
      }
      const dlg = this.$refs.gamePurchaseDialogs
      if (dlg && typeof dlg.open === 'function') {
        dlg.open(game)
      }
    }
  }
}
</script>

<style scoped>
.page {
  width: 100%;
  color: #1f2937;
}

.home-intro {
  margin: 10px 0 12px;
  padding: 2px 2px 4px;
  text-align: center;
}

.home-intro__title {
  margin: 0;
  color: #1f2d3d;
  font-size: clamp(24px, 3.3vw, 34px);
  font-weight: 760;
  letter-spacing: -0.03em;
  line-height: 1.18;
}

.home-intro__subtitle {
  max-width: 640px;
  margin: 8px auto 0;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.65;
}

.games-panel {
  padding-top: 2px;
}

.games__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 72px 24px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px dashed rgba(148, 163, 184, 0.45);
  color: #64748b;
  text-align: center;
}

.games__empty-icon {
  font-size: 36px;
  color: #409eff;
  opacity: 0.72;
}

.games__empty p {
  margin: 0;
  font-size: 14px;
}

.games__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(272px, 1fr));
  gap: 12px;
}

.game-card {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 2px 9px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  overflow: hidden;
  transition:
    box-shadow 180ms ease,
    border-color 180ms ease;
}

.game-card:hover {
  border-color: rgba(64, 158, 255, 0.28);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.09);
}

.game-card:focus-visible {
  outline: 2px solid #409eff;
  outline-offset: 3px;
}

.game-card__media {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: #eaf3ff;
}

.game-card__cover {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 220ms ease;
}

.game-card:hover .game-card__cover {
  transform: scale(1.02);
}

.game-card__shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 42%, rgba(30, 64, 175, 0.3) 100%);
  pointer-events: none;
}

.game-card__badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 1;
  padding: 5px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(64, 158, 255, 0.24);
  color: #409eff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.game-card__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 9px;
  padding: 12px 12px 11px;
}

.game-card__name {
  margin: 0;
  color: #1f2d3d;
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
  letter-spacing: -0.02em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.game-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}

.game-card__price {
  color: #337ecc;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.game-card__action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #409eff;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  transition: background 180ms ease;
}

.game-card:hover .game-card__action {
  background: #337ecc;
}

@media (max-width: 900px) {
  .home-intro {
    margin: 8px 0 10px;
  }

  .home-intro__title {
    font-size: clamp(22px, 5vw, 30px);
  }
}

@media (max-width: 640px) {
  .home-intro {
    margin: 6px 0 10px;
    padding: 0;
    text-align: center;
  }

  .home-intro__title {
    font-size: 22px;
  }

  .home-intro__subtitle {
    margin-top: 6px;
    font-size: 13px;
  }

  .games__grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .game-card__footer {
    flex-wrap: wrap;
  }
}
</style>

